/* ===== routes.ts — جدول المسارات وخريطة العناوين =====
   الرموز مطابقة حرفياً لما تستخدمه النسخة الأصلية في App.route (‎#plan‎ · ‎#uat‎ …).

   التحويل إلى صفحة واحدة (انظر docs/agents/integ.md «التحويل إلى صفحة واحدة»):
   الأصل كان ثلاث صفحات HTML والمسار في الـhash (Alber-PM-Platform.html#plan).
   الآن ملف HTML واحد والصفحة والمسار من المسار النظيف نفسه:
     /login            ← صفحة الدخول (Login.html في الأصل)
     /sign?t=<رمز>     ← بوابة التوقيع العامة (Sign.html في الأصل)
     /<مسار>           ← التطبيق: /dashboard · /plan · /uat …
   تعارض الاسم «sign»: للتطبيق مسار داخلي 'sign' (مركز التواقيع، تبويب «التواقيع»)
   وللبوابة العامة اسم Sign.html. البوابة تأخذ /sign لأن رابطها يُرسل بالبريد إلى
   موقّعين من خارج المنصة (أقصر وأوضح)، ومركز التواقيع يأخذ /signatures. الاسم
   الداخلي App.route يبقى 'sign' كما هو — الخريطة في العنوان وحده.

   لا شيء هنا يقرأ الـhash إلا تحويل الروابط القديمة «/#plan» ← «/plan» (مسار
   معروف بالضبط، في الجذر وحده) — فشظايا المصادقة (#access_token=… · ?code=…)
   لا تُفهم مساراً أبداً وتبقى لـ supabase-js يقرؤها ثم يمسحها بنفسه. */

import { PM_CONFIG } from '@/lib/config';

export const ROUTES = [
  'dashboard',
  'registry',
  'users',
  'project',
  'wizard',
  'plan',
  'track',
  'audit',
  'uat',
  'sign',
  'notifications',
  'settings',
] as const;

export type Route = (typeof ROUTES)[number];

export const DEFAULT_ROUTE: Route = 'dashboard';

const isRoute = (r: string): r is Route => (ROUTES as readonly string[]).includes(r);

/** مسار غير معروف يسقط إلى الرئيسية — نفس سلوك `else` في App.render الأصلية. */
export function normalizeRoute(raw: string | null | undefined): Route {
  const r = (raw || '').replace(/^[#/]/, '');
  return isRoute(r) ? r : DEFAULT_ROUTE;
}

/* ===== الصفحات ===== */

export type PageKind = 'app' | 'login' | 'sign';

/** مركز التواقيع في التطبيق: /signatures (لأن /sign للبوابة العامة). */
const ROUTE_SEGMENT: Partial<Record<Route, string>> = { sign: 'signatures' };
const SEGMENT_ROUTE: Record<string, Route> = { signatures: 'sign' };

/** أيّ صفحة يرسمها هذا المسار. كل ما ليس /login ولا /sign فهو التطبيق — ومنه
 *  المسار المجهول الذي يرسم الرئيسية بلا تبويب مضاء كالأصل. */
export function pageOf(pathname: string): PageKind {
  const seg = pathname.replace(/^\//, '');
  if (seg === PM_CONFIG.routes.login) return 'login';
  if (seg === PM_CONFIG.routes.sign) return 'sign';
  return 'app';
}

/** عنوان مسار التطبيق: 'plan' ⇒ '/plan' · 'sign' ⇒ '/signatures'. نصّ خام لغير المعروف. */
export function routePath(r: string): string {
  return '/' + ((isRoute(r) && ROUTE_SEGMENT[r]) || r);
}

/** App.route من المسار — نصّ خام كالأصل `(location.hash||'').replace('#','')||'dashboard'`:
 *  '/' ⇒ 'dashboard' · '/signatures' ⇒ 'sign' · '/foo' ⇒ 'foo' (تُرسم الرئيسية بلا تبويب). */
export function routeFromPath(pathname: string): string {
  const seg = pathname.replace(/^\//, '');
  return SEGMENT_ROUTE[seg] || seg || DEFAULT_ROUTE;
}

/** مسار التطبيق المعروف لهذا العنوان، أو null لصفحة الدخول/التوقيع أو مسار مجهول. */
export function knownRouteOfPath(pathname: string): Route | null {
  if (pageOf(pathname) !== 'app') return null;
  const r = routeFromPath(pathname);
  return isRoute(r) ? r : null;
}

/** App.route الحالي من شريط العنوان (نصّ خام — انظر routeFromPath). */
export function routeFromLocation(): string {
  return routeFromPath(window.location.pathname);
}

/** رابط قديم بالـhash في الجذر: «/#plan» ⇒ «/plan» (مع الاستعلام كما هو).
 *  مسار معروف بالضبط فقط — فـ«/#access_token=…» وأي شظية أخرى لا تُمسّ. */
export function legacyHashTarget(loc: { pathname: string; search: string; hash: string }): string | null {
  if (loc.pathname !== '/' || !loc.hash) return null;
  const r = loc.hash.replace(/^#/, '');
  return isRoute(r) ? routePath(r) + loc.search : null;
}
